import React, { createContext, useContext, useEffect, useState } from 'react';
import { Wrapper } from '@googlemaps/react-wrapper';

interface GoogleMapsContextType {
  isLoaded: boolean;
  error: string | null;
  requestLoad: () => void;
}

const GoogleMapsContext = createContext<GoogleMapsContextType>({
  isLoaded: false,
  error: null,
  requestLoad: () => {},
});

export const useGoogleMaps = () => useContext(GoogleMapsContext);

interface GoogleMapsProviderProps {
  children: React.ReactNode;
}

const API_KEY = 'AIzaSyDgfBHV2hAM-TQG0bwKm6hozl4ovm3-jow';

export const GoogleMapsProvider: React.FC<GoogleMapsProviderProps> = ({ children }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  const requestLoad = () => {
    setShouldLoad(true);
  };

  useEffect(() => {
    if (!shouldLoad) return;
    
    // If Google Maps is already loaded, mark as ready
    if (window.google && window.google.maps) {
      setIsLoaded(true);
      setError(null);
      return;
    }

    console.log('Starting Google Maps initialization...');

    // Set up a global callback for when Maps API loads
    const initMap = () => {
      console.log('Google Maps API loaded successfully');
      setIsLoaded(true);
      setError(null);
    };

    // Attach callback to window for the Google Maps loader
    (window as any).initGoogleMaps = initMap;

    // Check periodically if Google Maps has loaded
    const checkInterval = setInterval(() => {
      if (window.google && window.google.maps) {
        console.log('Google Maps detected in periodic check');
        clearInterval(checkInterval);
        initMap();
      }
    }, 100);

    // Clear interval after 30 seconds and set error
    const timeout = setTimeout(() => {
      clearInterval(checkInterval);
      if (!window.google || !window.google.maps) {
        console.error('Google Maps failed to load within 30 seconds');
        setError('Google Maps failed to load. Please check your API key and internet connection.');
      }
    }, 30000);

    return () => {
      clearInterval(checkInterval);
      clearTimeout(timeout);
    };
  }, [shouldLoad]);

  const contextValue = {
    isLoaded,
    error,
    requestLoad,
  };

  return (
    <GoogleMapsContext.Provider value={contextValue}>
      {shouldLoad ? (
        <Wrapper 
          apiKey={API_KEY}
          libraries={['places', 'geocoding']}
        >
          {children}
        </Wrapper>
      ) : (
        children
      )}
    </GoogleMapsContext.Provider>
  );
};